import {
  CARE_PLAN_NARRATIVE_PLACEHOLDERS,
  splitReplacement,
} from '@seen/core/models/care-plans/fillOnly'
import {
  DOMSerializer,
  type Fragment,
  DOMParser as PMDOMParser,
  type Node as PMNode,
  type Schema,
  Slice,
} from '@tiptap/pm/model'
import { Transform } from '@tiptap/pm/transform'

/* A fill-only replace that keeps some of the text it replaces word for word
 * and adds to it, split for showing. Under the fill-only rule a replace may only
 * swap placeholder text and add, and the model routinely restates a whole
 * labelled line to add a value to its end. Painted as an ordinary replace, that
 * strikes the label it keeps, and the card repeats it. Split, only what the
 * replacement drops is struck, and the card holds only what it adds. An accept
 * still writes the whole replacement over the whole range. */
export interface FillReplacement {
  // Ranges of the host document the replacement drops.
  dropped: { from: number; to: number }[]
  // The text the replacement adds, as HTML.
  additionHtml: string
  /* The whole replacement with its addition swapped for `editedHtml` — what an
   * island editing the addition alone writes. */
  compose: (editedHtml: string) => string
}

// A node's text from `from` to `to`, with the document position of each
// character: -1 for the line break standing between two blocks.
interface PositionedText {
  text: string
  positions: number[]
}

/* An inline node with no text of its own — a mention, a hard break — reads as
 * one object character, so it holds a token's place without matching a word. */
const INLINE_LEAF = '￼'

function positionedText(
  node: PMNode,
  from: number,
  to: number
): PositionedText {
  let text = ''
  const positions: number[] = []
  node.nodesBetween(from, to, (child, pos) => {
    if (child.isText) {
      const start = Math.max(from, pos)
      const end = Math.min(to, pos + child.nodeSize)
      for (let at = start; at < end; at++) {
        text += child.text?.[at - pos] ?? ''
        positions.push(at)
      }
      return false
    }
    if (child.isInline && child.isLeaf) {
      text += INLINE_LEAF
      positions.push(pos)
      return false
    }
    if (child.isTextblock && text !== '') {
      text += '\n'
      positions.push(-1)
    }
    return true
  })
  return { text, positions }
}

// Whether `html` parses to exactly one list node of one of `types`.
export function isOneList(
  schema: Schema,
  html: string,
  types: ReadonlySet<string>
): boolean {
  const { content } = parseHtml(schema, html)
  return (
    content.childCount === 1 && types.has(content.firstChild?.type.name ?? '')
  )
}

// Parsed in an inert document, so nothing in the HTML loads or runs.
function parseHtml(schema: Schema, html: string): PMNode {
  const body = new DOMParser().parseFromString(html, 'text/html').body
  return PMDOMParser.fromSchema(schema).parse(body, {
    preserveWhitespace: true,
  })
}

function serialize(schema: Schema, fragment: Fragment): string {
  const document = new DOMParser().parseFromString('', 'text/html')
  const container = document.createElement('div')
  container.appendChild(
    DOMSerializer.fromSchema(schema).serializeFragment(fragment, { document })
  )
  return container.innerHTML
}

// The replacement aligned against the text it replaces, when it keeps some of
// that text and adds to it.
function alignReplacement(doc: PMNode, from: number, to: number, html: string) {
  const schema = doc.type.schema
  const matched = positionedText(doc, from, to)
  const replacement = parseHtml(schema, html)
  const proposed = positionedText(replacement, 0, replacement.content.size)
  const split = splitReplacement(matched.text, proposed.text)
  const additionAt = proposed.positions[split.additionStart]
  const additionEnd = proposed.positions
    .filter((position) => position >= 0)
    .at(-1)
  if (
    split.kept === 0 ||
    additionAt === undefined ||
    additionAt < 0 ||
    additionEnd === undefined
  ) {
    return null
  }
  return { schema, matched, replacement, split, additionAt, additionEnd }
}

/* The document ranges of the runs a replacement drops. Runs start and end on a
 * token, never on a block's line break. A run takes the space before it with
 * it when that would otherwise be left doubled, or left in front of the
 * punctuation the run sat before — removing "(e.g. hint)" from
 * "Label (e.g. hint):" leaves "Label:", not "Label :". */
function droppedRuns(
  doc: PMNode,
  matched: PositionedText,
  runs: { start: number; end: number }[],
  from: number,
  to: number
): { from: number; to: number }[] {
  const charAt = (pos: number): string =>
    pos < 0 || pos >= doc.content.size
      ? ''
      : doc.textBetween(pos, pos + 1, ' ', INLINE_LEAF)
  const isSpace = (pos: number): boolean => /^\s$/.test(charAt(pos))
  return runs.map(({ start, end }) => {
    const run = {
      from: matched.positions[start] ?? from,
      to: (matched.positions[end - 1] ?? to - 1) + 1,
    }
    if (
      isSpace(run.from - 1) &&
      (isSpace(run.from - 2) || /^[^\s\p{L}\p{N}]$/u.test(charAt(run.to)))
    ) {
      run.from -= 1
    }
    return run
  })
}

// Placeholders that end in a colon of their own.
const flat = (text: string): string => text.replace(/\s+/g, ' ').trim()

const COLON_PLACEHOLDERS = CARE_PLAN_NARRATIVE_PLACEHOLDERS.map(flat).filter(
  (placeholder) => placeholder.endsWith(':')
)

/* A fill-only replace whose range ends on a label's colon — "(e.g. how to
 * contact):" after "Caregiver Preferences" — while the replacement leaves the
 * colon out: the colon is the label's, not the placeholder's, and the AI never
 * removes existing wording, so it goes back in after the last word the
 * replacement keeps, or at its start when it keeps none, ahead of what the
 * replacement adds. A colon that ends a placeholder itself — "Y/N ; If yes,
 * indicate dates and duration:" — is the placeholder's, and goes with it.
 * `html` comes back unchanged otherwise. */
export function keepingLabelColon(
  doc: PMNode,
  from: number,
  to: number,
  html: string
): string {
  const schema = doc.type.schema
  const matched = positionedText(doc, from, to)
  const colonEnd = matched.text.trimEnd().length
  if (
    !matched.text.trimEnd().endsWith(':') ||
    COLON_PLACEHOLDERS.some((placeholder) =>
      flat(matched.text).endsWith(placeholder)
    )
  ) {
    return html
  }
  const replacement = parseHtml(schema, html)
  const proposed = positionedText(replacement, 0, replacement.content.size)
  if (proposed.text.trim() === '' || proposed.text.trimEnd().endsWith(':')) {
    return html
  }
  const split = splitReplacement(matched.text, proposed.text)
  if (split.dropped.at(-1)?.end !== colonEnd) {
    return html
  }
  const keptEnd = proposed.text.slice(0, split.additionStart).trimEnd().length
  const at =
    keptEnd > 0
      ? (proposed.positions[keptEnd - 1] ?? -2) + 1
      : proposed.positions[0]
  if (at === undefined || at < 0) {
    return html
  }
  const next = proposed.text[keptEnd]
  const colon = next === undefined || /\s/.test(next) ? ':' : ': '
  return serialize(
    schema,
    new Transform(replacement).insert(at, schema.text(colon)).doc.content
  )
}

/* What a replacement adds after the text it replaces, as HTML, when it keeps
 * every word of that text and only adds; null otherwise. A mark the kept text
 * ends in and the addition carries on — a restated bold label whose bold runs
 * on over the answer — is the label's, not the answer's, so the addition is
 * returned without it. */
export function appendedHtml(
  doc: PMNode,
  from: number,
  to: number,
  html: string
): string | null {
  const aligned = alignReplacement(doc, from, to, html)
  if (!aligned || aligned.split.dropped.length > 0) {
    return null
  }
  return additionWithoutCarriedMarks(
    aligned.schema,
    aligned.replacement,
    aligned.additionAt
  )
}

/* The replacement from `additionAt` on, as HTML, without the marks the kept
 * text ends in: a restated bold label whose bold runs on over the answer is
 * the label's bold, not the answer's. */
function additionWithoutCarriedMarks(
  schema: Schema,
  replacement: PMNode,
  additionAt: number
): string {
  const end = replacement.content.size
  const lastKept = replacement.resolve(additionAt).nodeBefore
  const transform = new Transform(replacement)
  for (const mark of lastKept?.marks ?? []) {
    transform.removeMark(additionAt, end, mark.type)
  }
  return serialize(schema, transform.doc.slice(additionAt, end).content)
}

/* How to accept a fill-only replace without rewriting what it keeps: the runs
 * of the document it drops, and what it adds after the last word it keeps —
 * so the existing words stay exactly as stored, marks and all, even when the
 * model restated them without their bold. Null when the replacement keeps
 * none of the text, which is accepted as a replace like any other. */
export interface FillAcceptPlan {
  dropped: { from: number; to: number }[]
  // Undefined when the replacement only drops, a placeholder hint say.
  additionHtml?: string
  // Whether the replacement put a space between the kept text and the addition.
  spaced: boolean
}

export function fillAcceptPlan(
  doc: PMNode,
  from: number,
  to: number,
  html: string
): FillAcceptPlan | null {
  const schema = doc.type.schema
  const matched = positionedText(doc, from, to)
  const replacement = parseHtml(schema, html)
  const proposed = positionedText(replacement, 0, replacement.content.size)
  const split = splitReplacement(matched.text, proposed.text)
  if (split.kept === 0) {
    return null
  }
  const dropped = droppedRuns(doc, matched, split.dropped, from, to)
  const additionAt = proposed.positions[split.additionStart]
  if (additionAt === undefined || additionAt < 0) {
    return { dropped, spaced: false }
  }
  return {
    dropped,
    additionHtml: additionWithoutCarriedMarks(schema, replacement, additionAt),
    spaced: /\s/.test(proposed.text[split.additionStart - 1] ?? ''),
  }
}

/* The split of `html` against the text the range `from`–`to` of `doc` holds,
 * or null when the replacement keeps none of that text or adds nothing to it,
 * where the ordinary rendering — the whole range struck, the whole
 * replacement in the card — already reads right. */
export function splitFillReplacement(
  doc: PMNode,
  from: number,
  to: number,
  html: string
): FillReplacement | null {
  const aligned = alignReplacement(doc, from, to, html)
  if (!aligned) {
    return null
  }
  const { schema, matched, replacement, split, additionAt, additionEnd } =
    aligned
  return {
    dropped: droppedRuns(doc, matched, split.dropped, from, to),
    // As an accept will write it: see `fillAcceptPlan`.
    additionHtml: additionWithoutCarriedMarks(schema, replacement, additionAt),
    compose: (editedHtml) => {
      const edited = parseHtml(schema, editedHtml)
      try {
        // Open at both ends, so the edit's first paragraph continues the line
        // the kept text ends on rather than opening one of its own.
        const composed = new Transform(replacement).replace(
          additionAt,
          additionEnd + 1,
          new Slice(edited.content, 1, 1)
        ).doc
        return serialize(schema, composed.content)
      } catch {
        return `${serialize(schema, replacement.slice(0, additionAt).content)}${editedHtml}`
      }
    },
  }
}
